import { useCallback, useState } from "react";
import { api, ApiError, type Child } from "../lib/api";
import { useLiveData } from "../lib/useLiveData";
import { Field } from "./Setup";

type Chore = {
  id: string;
  name: string;
  icon: string;
  childIds: string[];
  recurrence: { type: string; days_of_week?: number[]; count?: number };
  isRequired: boolean;
  weight: number;
  approvalMode: string;
  sortOrder: number;
};

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/* Chore definitions.
 *
 * Weight is exposed because the threshold counts WEIGHT, not chores: cleaning
 * a bathroom should not be worth the same as putting shoes away. Approval mode
 * is per-chore rather than global for the same reason -- "made bed" can be
 * self-certified while "cleaned the litter box" cannot.
 */
export function ChoreManager({ onBack }: { onBack: () => void }) {
  const [chores, setChores] = useState<Chore[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [name, setName] = useState("");
  const [assigned, setAssigned] = useState<string[]>([]);
  const [recurrence, setRecurrence] = useState("daily");
  const [days, setDays] = useState<number[]>([]);
  const [weight, setWeight] = useState("1");
  const [autoApprove, setAutoApprove] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, kids] = await Promise.all([
        api.get<Chore[]>("/chores"),
        api.get<Child[]>("/children"),
      ]);
      setChores(c);
      setChildren(kids);
      if (assigned.length === 0 && kids.length > 0) setAssigned([kids[0].id]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load jobs.");
    }
    // `assigned` is intentionally not a dependency: seeding it once on first
    // load must not re-run and stomp the parent's selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLiveData(load);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (assigned.length === 0) {
      setError("Pick at least one kid.");
      return;
    }
    if (recurrence === "days_of_week" && days.length === 0) {
      setError("Pick at least one day.");
      return;
    }
    setBusy("new");
    setError(null);
    try {
      await api.post("/chores", {
        name: name.trim(),
        childIds: assigned,
        recurrence:
          recurrence === "days_of_week"
            ? { type: "days_of_week", daysOfWeek: days }
            : { type: recurrence },
        weight: Math.max(1, parseInt(weight, 10) || 1),
        isRequired: true,
        approvalMode: autoApprove ? "auto" : "parent",
      });
      setName("");
      setWeight("1");
      setDays([]);
      setRecurrence("daily");
      setAutoApprove(false);
      setAdding(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not add the job.");
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api.del(`/chores/${id}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not remove it.");
    } finally {
      setBusy(null);
    }
  }

  function describe(chore: Chore) {
    const who = chore.childIds
      .map((id) => children.find((c) => c.id === id)?.displayName)
      .filter(Boolean)
      .join(", ");
    const when =
      chore.recurrence.type === "daily"
        ? "every day"
        : chore.recurrence.type === "days_of_week"
          ? (chore.recurrence.days_of_week ?? []).map((d) => DAYS[d]).join(", ")
          : chore.recurrence.type === "once"
            ? "once"
            : `${chore.recurrence.count ?? 1}× a week`;
    const extras = [
      when,
      chore.weight > 1 ? `counts as ${chore.weight}` : null,
      chore.approvalMode === "auto" ? "no check needed" : null,
    ].filter(Boolean);
    return `${who || "nobody"} · ${extras.join(" · ")}`;
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <button onClick={onBack} style={{ justifySelf: "start", minHeight: 44, fontSize: 14 }}>
        ← Back
      </button>

      <section className="card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 20 }}>Jobs</h2>
          {!adding && (
            <button
              onClick={() => setAdding(true)}
              disabled={children.length === 0}
              style={{ minHeight: 44, fontSize: 14 }}
            >
              + Add a job
            </button>
          )}
        </div>

        {children.length === 0 && (
          <p style={{ color: "var(--ink-2)", margin: 0 }}>
            Add a kid first, then you can give them jobs.
          </p>
        )}

        {chores.length === 0 && children.length > 0 && !adding && (
          <p style={{ color: "var(--ink-2)", margin: 0 }}>
            No jobs yet. Allowance is earned by finishing jobs, so add a few to
            get started.
          </p>
        )}

        {chores.length > 0 && (
          <div style={{ display: "grid", gap: 8 }}>
            {chores.map((chore) => (
              <div
                key={chore.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "10px 12px",
                  border: "2px solid var(--paper-edge)",
                  borderRadius: "var(--r-md)",
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      fontFamily: "var(--font-display)",
                      fontSize: 16,
                    }}
                  >
                    {chore.name}
                  </span>
                  <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                    {describe(chore)}
                  </span>
                </span>
                <button
                  onClick={() => remove(chore.id)}
                  disabled={busy === chore.id}
                  style={{ minHeight: 44, fontSize: 13, padding: "0 12px" }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {adding && (
        <form onSubmit={create} className="card" style={{ display: "grid", gap: 14 }}>
          <h3 style={{ fontSize: 17 }}>New job</h3>

          <Field label="What is it?">
            <input value={name} onChange={(e) => setName(e.target.value)}
                   required maxLength={60} placeholder="Make your bed" autoFocus />
          </Field>

          <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 600, fontSize: 14, padding: 0, marginBottom: 8 }}>
              Who does it?
            </legend>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {children.map((kid) => {
                const on = assigned.includes(kid.id);
                return (
                  <button
                    key={kid.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setAssigned(
                        on ? assigned.filter((i) => i !== kid.id) : [...assigned, kid.id],
                      )
                    }
                    style={{
                      minHeight: 44, fontSize: 14, boxShadow: "none",
                      background: on ? "var(--save-soft)" : "var(--paper)",
                      borderColor: on ? "var(--save)" : "var(--paper-edge)",
                    }}
                  >
                    {kid.displayName}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <Field label="How often?">
            <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
              <option value="daily">Every day</option>
              <option value="days_of_week">Certain days</option>
              <option value="once">Just once</option>
            </select>
          </Field>

          {recurrence === "days_of_week" && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {DAYS.map((label, index) => {
                const on = days.includes(index);
                return (
                  <button
                    key={label}
                    type="button"
                    aria-pressed={on}
                    onClick={() =>
                      setDays(on ? days.filter((d) => d !== index) : [...days, index])
                    }
                    style={{
                      minHeight: 44, padding: "0 12px", fontSize: 13, boxShadow: "none",
                      background: on ? "var(--save-soft)" : "var(--paper)",
                      borderColor: on ? "var(--save)" : "var(--paper-edge)",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          <Field
            label="How much does it count?"
            hint="A big job can be worth more than a small one."
          >
            <select value={weight} onChange={(e) => setWeight(e.target.value)}>
              <option value="1">Normal</option>
              <option value="2">Counts double</option>
              <option value="3">Counts triple</option>
              <option value="5">Big job (counts as 5)</option>
            </select>
          </Field>

          <label style={{ display: "flex", gap: 10, alignItems: "center", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={autoApprove}
              onChange={(e) => setAutoApprove(e.target.checked)}
              style={{ width: 22, height: 22, minHeight: 0, flex: "0 0 auto" }}
            />
            <span style={{ fontSize: 14 }}>
              Trust them &mdash; no need for me to check this one
            </span>
          </label>

          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={busy === "new"} style={{ flex: 1 }}>
              {busy === "new" ? "Adding…" : "Add job"}
            </button>
            <button type="button" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && (
        <p role="alert" style={{ color: "var(--bad)", fontWeight: 600, margin: 0 }}>
          {error}
        </p>
      )}
    </div>
  );
}
